import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/auth';
import { BriefcaseIcon, ClockIcon } from '@heroicons/react/24/outline';
import { PlusIcon } from '@heroicons/react/20/solid';
import { updateTimeSlot } from '../services/api_working_hours';
import EditBreaks from './edit_breaks';
import { validateBreak, validateBreaks, validateTimeSlots, formatTime } from '../utils/timeslots';
import Swal from 'sweetalert2';


const EditTimeSlot = ({ onCancelEdit, onClose, shiftPreview, timeSlotObject, onUpdatedTimeSlot, timeSlots }) => {
  const { token } = useContext(AuthContext);
  const [initialWorkStartTime, setInitialWorkStartTime] = useState(formatTime(timeSlotObject.start_at));
  const [initialWorkEndTime, setInitialWorkEndTime] = useState(formatTime(timeSlotObject.end_at));
  const [workStartTime, setWorkStartTime] = useState(formatTime(timeSlotObject.start_at));
  const [workEndTime, setWorkEndTime] = useState(formatTime(timeSlotObject.end_at));
  const [error, setError] = useState('');
  const [workTimeError, setWorkTimeError] = useState('');
  const [creatingPause, setCreatingPause] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setInitialWorkStartTime(formatTime(timeSlotObject.start_at));
    setInitialWorkEndTime(formatTime(timeSlotObject.end_at));
  }, [timeSlotObject]);

  const handleWorkStartTimeChange = (e) => {
    let inputValue = e.target.value;
    inputValue = inputValue.replace(/\D/g, '');
    inputValue = inputValue.slice(0, 4);

    let formattedTime = inputValue.replace(/(\d{2})(\d{0,2})/, "$1:$2");
  
    if (inputValue.length <= 2 && e.target.value.length < formattedTime.length) {
      formattedTime = formattedTime.replace(":", "");
    }
  
    setWorkStartTime(formattedTime);

    if (workEndTime && formattedTime >= workEndTime) {
      setWorkTimeError("L'orario di inizio deve essere precedente all'orario di fine.");
    } else {
      setWorkTimeError('');
      setError('');
    }
  };

  const handleWorkEndTimeChange = (e) => {
    let inputValue = e.target.value;
  
    inputValue = inputValue.replace(/\D/g, '');
    inputValue = inputValue.slice(0, 4);
  
    let formattedTime = inputValue.replace(/(\d{2})(\d{0,2})/, "$1:$2");
  
    if (inputValue.length <= 2 && e.target.value.length < formattedTime.length) {
      formattedTime = formattedTime.replace(":", "");
    }
  
    setWorkEndTime(formattedTime);

    if (workStartTime && formattedTime <= workStartTime) {
      setWorkTimeError("L'orario di fine deve essere successivo all'orario di inizio.");
      return;
    } else {
      setWorkTimeError('');
      setError(''); 
    }
  };

  const handleWorkTimeBlur = (field) => {
    let formattedTime = field === 'start' ? workStartTime : workEndTime;
  
    formattedTime = formattedTime.replace(/\D/g, '');
      
    while (formattedTime.length < 4) {
      formattedTime += '0';
    }
  
    formattedTime = formattedTime.slice(0, 4);
  
    const hours = formattedTime.slice(0, 2);
    const minutes = formattedTime.slice(2);
    formattedTime = `${hours}:${minutes}`;
  
    const isValidTime = /^([01]?[0-9]|2[0-4]):[0-5][0-9]$/.test(formattedTime);
  
    if (!isValidTime) {
      if (field === 'start') {
        setWorkStartTime('');
      } else {
        setWorkEndTime('');
      }
      return;
    }
  
    if (field === 'start') {
      setWorkStartTime(formattedTime);
    } else {
      setWorkEndTime(formattedTime);
    }
  };

  const handleCancelCreatingPause = () => {
      setCreatingPause(false); 
  };

  const handleSaveTimeSlot = async () => {
    try {
      setIsSaving(true);

      const updatedTimeSlot = {
        ...timeSlotObject,
        start_at: workStartTime,
        end_at: workEndTime,
      };
      
      const timeSlotToCheck = timeSlots.filter(ts => ts.id !== timeSlotObject.id)
      
      try {
        validateTimeSlots(workStartTime, workEndTime, timeSlotToCheck);
        
        validateBreaks(workStartTime, workEndTime, timeSlotObject.breaks)
      }
      catch (error) {
        setError(error.message)
        setWorkStartTime(initialWorkStartTime);
        setWorkEndTime(initialWorkEndTime); 
        return;
      }

      await updateTimeSlot(token, timeSlotObject.id, updatedTimeSlot,);

      onUpdatedTimeSlot(updatedTimeSlot);      
      Swal.fire({
        position: "center",
        icon: "success",
        title: "Salvato!",
        showConfirmButton: false,
        timer: 1500
      });
      } catch (error) {
        console.error('Errore durante l\'aggiornamento della fascia oraria', error);

        let errorMessage = "Errore durante l'aggiornamento della fascia oraria";

        if (error.response && error.response.data) {
          const errorData = error.response.data;
          const errorMessages = [];

          Object.keys(errorData).forEach((key) => {
            const messages = errorData[key];
            if (Array.isArray(messages)) {
              errorMessages.push(...messages);
            } else if (typeof messages === "string") {
              errorMessages.push(messages);
            }
          });

          if (errorMessages.length > 0) {
            errorMessage = errorMessages.join(" ");
          }
        }

        setError(errorMessage);

        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: errorMessage,
        }
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelClick = () => {
    onCancelEdit();
  };

  const handleUpdatePauseTimes = (updatedPauseTimes) => {
    const updateTimeSlot = {...timeSlotObject, breaks: updatedPauseTimes}
    onUpdatedTimeSlot(updateTimeSlot); 
  };

  const handleAddPause = () => {
    if (!workStartTime || !workEndTime) {
      setWorkTimeError("Prima di aggiungere una pausa, inserisci l'orario di lavoro.");
      return;
    }
    setCreatingPause(true);
  };

  return (
    <>
      {timeSlotObject && (
        <div className='border border-solid-2 border-gray-200 shadow-md rounded-md my-5 w-full bg-white'>
          <div className='px-4 mt-4'>
            <label htmlFor="parent" className="text-sm font-medium text-gray-700 m-0 col-span-1 flex items-center">Modifica Fascia Oraria</label>
            <p className="text-xs font-medium text-gray-700 m-0 col-span-1 flex items-center mt-2">Inserisci il nuovo orario e seleziona 'Salva' sul campo che desideri modificare</p>
            {error && (
              <div className="grid grid-cols-1 mx-4">
                <p className="text-red-500 text-sm text-center">{error}</p>
              </div>
            )}
            <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 mx-0 pt-7 gap-4 items-center'>
              <div className='col-span-1 lg:col-span-2 flex items-center'>
                 <label htmlFor="start-time" className="w-24 text-sm font-medium text-gray-700 flex items-center space-x-1">
                  <BriefcaseIcon className='h-5 w-5 mr-2' />
                  Dalle ore:
                </label>
                <div className="relative inline-block w-fit">
                  <input 
                    type="text"
                    placeholder='hh:mm' 
                    value={workStartTime}
                    onInput={handleWorkStartTimeChange}
                    onChange={(e) => setWorkStartTime(e.target.value)}
                    onBlur={() => handleWorkTimeBlur('start')}
                    className="mt-1 p-1 pl-4 border-gray-200 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-left block w-24 shadow-md sm:text-sm required" 
                    maxLength={4}
                  />
                  <span className="absolute top-1/2 right-2 transform -translate-y-1/2">
                    <ClockIcon className='w-4 h-4' />
                  </span>
                </div>
              </div>
              <div className='col-span-1 lg:col-span-2 flex items-center'>
                <label htmlFor="end-time" className="w-24 text-sm font-medium text-gray-700 flex items-center space-x-1">
                  <BriefcaseIcon className='h-5 w-5 mr-2' />
                  Alle ore:
                </label>
                <div className="relative inline-block w-fit">
                  <input 
                    type="text"
                    placeholder='hh:mm' 
                    value={workEndTime}
                    onInput={handleWorkEndTimeChange}  
                    onChange={(e) => setWorkEndTime(e.target.value)}
                    onBlur={() => handleWorkTimeBlur('end')}
                    className="mt-1 p-1 pl-4 border-gray-200 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-left block w-24 shadow-md sm:text-sm required"
                  />
                  <span className="absolute top-1/2 right-2 transform -translate-y-1/2">
                    <ClockIcon className='w-4 h-4' />
                  </span>
                </div>
              </div>
              <div className='col-span-1 sm:col-span-2 lg:col-span-1 flex items-center justify-center ml-2'>
                <button
                  onClick={handleSaveTimeSlot}
                  className="bg-indigo-600 text-white text-sm p-2 rounded-md hover:bg-indigo-500 focus:outline-none w-24 h-8 flex items-center justify-center uppercase font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  disabled={isSaving}
                >
                  {isSaving ? 'Salvataggio...' : 'Salva'}
                </button>
              </div>
            </div>
          </div>

          {workTimeError && (
            <p className="text-red-500 text-sm justify-center flex pt-8 ">{workTimeError}</p>
          )}

          <div className='grid grid-cols-1 lg:grid-cols-5 px-4 pt-7 gap-2 items-center'>
            <div className='col-span-5 flex items-center'>
              <label htmlFor="parent" className="block text-sm font-medium text-gray-700 m-0">Modifica Pausa</label>
                <button onClick={handleAddPause} className="ml-3 border rounded-md bg-white py-1 px-2 mr-2 text-center text-sm font-semibold text-gray-700 shadow-md hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 flex items-center">
                  <PlusIcon className='w-5 h-5 text-indigo-500 mr-1' />  
                  AGGIUNGI PAUSA
                </button>
            </div>
          </div>

          <div className='w-full'>
            <EditBreaks 
              timeSlotEditBreak={timeSlotObject} 
              handleAddPause={handleAddPause}
              creatingPause={creatingPause}
              onCancelCreatingPause={handleCancelCreatingPause}
              onUpdatePauseTimes={handleUpdatePauseTimes}
            />
          </div>
          
          <div className='justify-center flex mt-7 mb-4'>
            <button onClick={handleCancelClick} className="bg-gray-300 text-gray-700 text-sm px-4 py-2 rounded-md ml-4 hover:bg-gray-200 focus:outline-none focus:bg-gray-200 w-24 font-semibold uppercase focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">Chiudi</button>
          </div>
        </div>
      )}
    </>
  );
}

export default EditTimeSlot;

